import { Link } from 'react-router-dom';
import { dateHeureDeTunis } from '@rh/shared';
import { useFile } from '../horsligne/contexte-file';
import { heureDeTunis, jourDeTunis, noteEnFrancais } from '../horsligne/textes';
import type { EnvoiContact, SaisieAppelLocale } from '../horsligne/types';
import { corpsAppel } from '../horsligne/validation';

/**
 * Clôture ou évaluation mise en file sans réponse du serveur (docs/UI.md
 * § Écran 3, mode hors ligne). La fiche n'est pas lisible hors ligne : on ne
 * navigue pas, le formulaire laisse la place à ce résumé, qui passe en direct à
 * « envoyé » dès que le serveur l'a reçu.
 */

function resumeAppel(saisie: SaisieAppelLocale): string {
  let corps;
  try {
    corps = corpsAppel(saisie);
  } catch {
    corps = null;
  }
  const morceaux: string[] = [];
  if (saisie.joignable !== null) morceaux.push(saisie.joignable ? 'Joint' : 'Injoignable');
  if (saisie.joignable) {
    if (saisie.disponible !== null) morceaux.push(saisie.disponible ? 'disponible de suite' : 'pas disponible de suite');
    if (saisie.potentiel !== null) morceaux.push(`pastille ${saisie.potentiel}`);
    if (corps?.rdvLe) morceaux.push(`rendez-vous le ${dateHeureDeTunis(corps.rdvLe)}`);
  }
  if (corps?.rappelLe) morceaux.push(`rappel le ${dateHeureDeTunis(corps.rappelLe)}`);
  return morceaux.join(' · ');
}

export function SaisieMiseEnFile({
  envoi,
  enLigne,
  envoyeLe = null,
  onOuvrirFiche,
}: {
  envoi: EnvoiContact;
  enLigne: boolean;
  /** Heure de réception par le serveur, une fois l'envoi fait. */
  envoyeLe?: number | null;
  onOuvrirFiche(): void;
}) {
  const { envoyerMaintenant } = useFile();
  const fin = envoi.fin;
  if (!fin) return null;
  const appel = fin.genre === 'cloture';

  if (envoyeLe !== null) {
    return (
      <section className="saisie-en-file saisie-en-file--envoyee" role="status">
        <h3>{appel ? 'Appel clôturé et envoyé' : 'Évaluation envoyée'}</h3>
        <p>Reçu par le serveur à {heureDeTunis(envoyeLe)}.</p>
        <div className="actions">
          <button type="button" className="bouton" onClick={onOuvrirFiche}>
            Ouvrir la fiche
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="saisie-en-file" role="status">
      {fin.genre === 'cloture' ? (
        <>
          <h3>Appel clôturé sur ce téléphone</h3>
          <p>Il sera envoyé au retour du réseau, daté de {heureDeTunis(fin.termineLe)}, l’heure de votre clôture.</p>
          {envoi.saisie?.type === 'telephonique' && <p className="meta">{resumeAppel(envoi.saisie)}</p>}
          <p className="mention">
            La fiche du candidat ne le montrera qu’une fois envoyé. Sans envoi, la saisie reste sur ce téléphone
            jusqu’au {jourDeTunis(envoi.expireLe)}.
          </p>
        </>
      ) : (
        <>
          <h3>Évaluation validée sur ce téléphone</h3>
          <p>
            Note {noteEnFrancais(fin.noteAffichee)} / 5 · barème v{fin.bareme.version}. Envoi au retour du réseau,
            daté de {heureDeTunis(fin.evalueeLe)} ; elle sera figée dès sa réception.
          </p>
          <p className="mention">Sans envoi, la saisie reste sur ce téléphone jusqu’au {jourDeTunis(envoi.expireLe)}.</p>
        </>
      )}
      <div className="actions">
        <Link className="bouton-second lien-bouton" to="/envois">
          Voir les envois en attente
        </Link>
        <Link className="bouton-second lien-bouton" to={`/candidatures/${envoi.candidatureId}`}>
          Retour à la fiche
        </Link>
        {enLigne && (
          <button type="button" className="bouton" onClick={envoyerMaintenant}>
            Envoyer maintenant
          </button>
        )}
      </div>
    </section>
  );
}
